import _ from 'lodash';

import {DEFAULT_LINE_PLOT_MAX_GROUP_RUNS} from '../../components/WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {
  QueryToRunsDataQueryParams,
  RunsDataQuery,
  toRunsDataQuery,
} from '../../containers/RunsDataLoader';
import {SmoothingType} from '../../generated/graphql';
import {
  BucketedHistorySpec,
  HistorySpec,
  MetricTransformation,
} from '../../state/runs/types';
import {CHART_SAMPLES} from '../../util/constants';
import {Expression, metricsInExpression} from '../../util/expr';
import * as Filters from '../../util/filters';
import {Filter} from '../../util/filterTypes';
import * as PanelHelpers from '../../util/panelHelpers';
import {Query} from '../../util/queryTypes';
import {keyPossibilities, shouldAddDotValue} from '../../util/runs';
import {Key} from '../../util/runTypes';
import {DEFAULT_X_AXIS_SETTINGS} from '../WorkspaceDrawer/Settings/runLinePlots/linePlotDefaults';
import {isSystemMetric} from './../../util/metrics';
import {getExtraFields} from './queryConstructor/getSectionFields';
import {RunsLinePlotConfig} from './types';
import {isMetricKeyAbsoluteRuntime} from './zoom/utils';

export interface Range {
  min: number | null;
  max: number | null;
}

function getMetricTransformations(
  runsLinePlotConfig: RunsLinePlotConfig,
  metricKey: string
): {
  [key: string]: MetricTransformation[];
} {
  if (
    runsLinePlotConfig.metrics != null &&
    runsLinePlotConfig.smoothingWeight &&
    runsLinePlotConfig.smoothingWeight > 0
  ) {
    let transformation: MetricTransformation;
    if (runsLinePlotConfig.smoothingType === SmoothingType.Exponential) {
      // Clamp smoothing weight to be between 0 and 1
      const clampedSmoothingWeight = Math.min(
        0.99,
        runsLinePlotConfig.smoothingWeight
      );
      transformation = {
        type: 'ema',
        parameters: {
          alpha: clampedSmoothingWeight,
          zeroDebias: true,
        },
      };
    } else if (runsLinePlotConfig.smoothingType === SmoothingType.Gaussian) {
      transformation = {
        type: 'gaussian',
        parameters: {
          sigma: runsLinePlotConfig.smoothingWeight,
        },
      };
    } else if (runsLinePlotConfig.smoothingType === SmoothingType.Average) {
      transformation = {
        type: 'running_average',
        parameters: {
          windowSize: runsLinePlotConfig.smoothingWeight,
        },
      };
    } else if (
      runsLinePlotConfig.smoothingType ===
        SmoothingType.ExponentialTimeWeighted ||
      runsLinePlotConfig.smoothingType === undefined
    ) {
      transformation = {
        type: 'time_weighted_ema',
        parameters: {
          alpha: runsLinePlotConfig.smoothingWeight,
        },
      };
    } else {
      return {};
    }

    return {[metricKey]: [transformation]};
  }

  return {};
}

export function runsLinePlotTransformQuery(options: {
  defaultMaxRuns: number;
  enableBackendSmoothing?: boolean;
  isFullFidelity?: boolean;
  parsedExpressions: {
    expressions: Expression[] | undefined;
    xExpression: Expression | undefined;
  };
  query: Query;
  runsLinePlotConfig: RunsLinePlotConfig;
  useCappedSystemMetrics?: boolean;
  xStepRange: Range | null;
}): RunsDataQuery {
  const {
    defaultMaxRuns,
    enableBackendSmoothing = false,
    isFullFidelity = false,
    parsedExpressions,
    query,
    runsLinePlotConfig,
    xStepRange,
  } = options;
  const singleRun = Boolean(query.runName);
  const isGrouped = PanelHelpers.isGrouped(query, runsLinePlotConfig);

  let result;
  if (singleRun) {
    result = toRunsDataQuery(query, undefined, {
      panelType: 'Run History Line Plot',
    });
  } else {
    const queryToDataQueryParams: QueryToRunsDataQueryParams = {
      selectionsAsFilters: true,
    };

    if (
      runsLinePlotConfig.metrics != null &&
      runsLinePlotConfig.metrics.length > 0
    ) {
      const filters: Array<Filter<Key>> = runsLinePlotConfig.metrics
        .slice(0, 50)
        .map(metric => ({
          key: {section: 'keys_info', name: metric},
          op: '!=',
          value: null,
        }));
      queryToDataQueryParams.mergeFilters = Filters.Or(filters);
    }

    result = toRunsDataQuery(query, queryToDataQueryParams, {
      panelType: 'Run History Line Plot',
    });
  }

  if (runsLinePlotConfig.useRunsTableGroupingInPanels === false) {
    result.queries = result.queries.map(q => ({...q, grouping: []}));
  }

  result.disabled = true;

  const extraFields = getExtraFields(
    runsLinePlotConfig,
    parsedExpressions,
    query
  );

  result.configKeys = extraFields
    .filter(key => key.section === 'config')
    .map(key => {
      // If the key has an array index, we only want the base name, and we'll deal with the nesting in the legend
      if (shouldAddDotValue(key)) {
        return keyPossibilities(key).map(k => k.name.split('[')?.[0]);
      }
      return key.name.split('[')?.[0];
    })
    .flat();

  result.summaryKeys = extraFields
    .filter(key => key.section === 'summary')
    .map(key => key.name);

  if (runsLinePlotConfig.metrics != null) {
    // We load the history data for the graphs
    // We sample values where an individual metric and the xAxis has values
    // Some users might prefer to sample history rows where all of the metrics
    // and the xAxis has a value, but this will cause some graphs to not display
    // if for example a user logs test data in one step and training data in another
    // and then wants to plot the two metrics in a single graph.
    result.disabled = false;
    result.history = true;

    const xAxis = runsLinePlotConfig.xAxis ?? DEFAULT_X_AXIS_SETTINGS.xAxis;

    const yAxisExprKeys: string[] = [];
    for (const expr of parsedExpressions.expressions ?? []) {
      yAxisExprKeys.push(...metricsInExpression(expr));
    }
    const xAxisExprKeys: string[] =
      parsedExpressions.xExpression != null
        ? metricsInExpression(parsedExpressions.xExpression)
        : [];

    const xAxisHistoryKeys: string[] = []; // history keys we need besides the metric
    if (!getHasSystemMetrics(runsLinePlotConfig)) {
      xAxisHistoryKeys.push('_step');
    }
    if (xAxis === '_absolute_runtime') {
      // we calculate absolute wall time from the start of the run
      // we need both timestamp and runtime to do this
      xAxisHistoryKeys.push('_timestamp');
      xAxisHistoryKeys.push('_runtime');
    } else {
      // this is the normal case
      xAxisHistoryKeys.push(xAxis);
    }
    xAxisHistoryKeys.push(...xAxisExprKeys);

    let numberOfPoints = CHART_SAMPLES;
    if (isFullFidelity) {
      // This will be set later by useBucketedData
      // if we're capping system metrics be more aggressive about throttling
      // TODO: keep this from being overwritten downstream
      numberOfPoints = options.useCappedSystemMetrics ? 250 : 1000;
    }
    if (xAxis === '_step') {
      runsLinePlotConfig.xAxis = xAxis;
    }

    const specs = _.uniq([
      ...runsLinePlotConfig.metrics,
      ...yAxisExprKeys,
      ...xAxisExprKeys,
    ]).map(metricKey =>
      createGorillaHistorySpec({
        enableBackendSmoothing,
        isGrouped,
        metricKey,
        numberOfPoints,
        runsLinePlotConfig,
        useGorillaOutliers: isFullFidelity,
        xAxis,
        xAxisHistoryKeys,
        xStepRange,
      })
    );

    if (isFullFidelity) {
      result.bucketedHistorySpecs = specs as BucketedHistorySpec[];
    } else {
      result.historySpecs = specs as HistorySpec[];
    }

    // order of specificity:
    // if an explicit limit is on the config (such as if a user has set the max runs value in the UI), use that
    // otherwise default to the defaultMaxRuns value which can vary by custom default.
    // But we shouldn't let the user set a limit that's greater than the defaultMaxRuns value.
    result.page = {
      size: singleRun
        ? 1
        : Math.min(runsLinePlotConfig.limit ?? defaultMaxRuns, defaultMaxRuns),
    };

    if (isGrouped) {
      // We need the metadata for grouping because we do it locally
      // TODO: move grouping to server
      // result.disableMeta = false;

      // optionally compute group statistics over all runs instead of sub-sampling
      result.page.size =
        runsLinePlotConfig.groupRunsLimit ?? DEFAULT_LINE_PLOT_MAX_GROUP_RUNS;
      // Disable grouping for this query, we'll do it ourselves.
      result.queries = result.queries.map(q => ({
        ...q,
        grouping: [],
        maxGroups: runsLinePlotConfig.limit,
      }));
    }
  }

  return result;
}

type CreateGorillaHistorySpecParams = {
  enableBackendSmoothing?: boolean;
  isGrouped?: boolean;
  metricKey: string;
  numberOfPoints: number;
  runsLinePlotConfig?: RunsLinePlotConfig;
  useGorillaOutliers: boolean;
  xAxis: string;
  xAxisHistoryKeys: string[];
  xStepRange: Range | null;
};

export function createGorillaHistorySpec({
  enableBackendSmoothing,
  isGrouped,
  metricKey,
  numberOfPoints,
  runsLinePlotConfig,
  useGorillaOutliers,
  xAxis,
  xAxisHistoryKeys,
  xStepRange,
}: CreateGorillaHistorySpecParams): HistorySpec | BucketedHistorySpec {
  let throttledSampledGroupLines = numberOfPoints;

  // If we're grouping and in sampling, we allow a larger number of runs that usual
  // this creates backend load so we're going to ramp down the number of points per
  // run to compensate
  if (isGrouped && !useGorillaOutliers) {
    throttledSampledGroupLines = Math.min(1000, throttledSampledGroupLines);
  }

  const spec = {
    keys: _.uniq([...xAxisHistoryKeys, metricKey]),
    samples: throttledSampledGroupLines,
    // The API won't accept zoom ranges in decimal form - they must be integers, so make sure to round
    ...(xStepRange != null
      ? {
          minStep: Number.isFinite(xStepRange.min)
            ? // @ts-expect-error TS doesn't handle this check correctly
              Math.floor(xStepRange.min)
            : undefined,
          maxStep: Number.isFinite(xStepRange.max)
            ? // @ts-expect-error TS doesn't handle this check correctly
              Math.ceil(xStepRange.max)
            : undefined,
        }
      : {}),
  };

  if (useGorillaOutliers) {
    /**
     * When querying against a custom axis, we modify the bucketedHistorySpec to use `minX` and `maxX` instead of `minStep` and `maxStep`. This is because minStep and maxStep are reserved for querying against _step only, and cannot support things like non-integer values in those positions.
     */
    const notASupportedCustomMetric =
      xAxis === '_step' || isMetricKeyAbsoluteRuntime(xAxis);

    const customXRange = notASupportedCustomMetric
      ? {}
      : {
          minStep: undefined,
          maxStep: undefined,
          minX:
            xStepRange && Number.isFinite(xStepRange.min)
              ? (xStepRange.min ?? undefined)
              : undefined,
          maxX:
            xStepRange && Number.isFinite(xStepRange.max)
              ? (xStepRange.max ?? undefined)
              : undefined,
        };

    return {
      ...spec,
      ...customXRange,
      bins: Math.floor(numberOfPoints),
      xAxis: isMetricKeyAbsoluteRuntime(xAxis) ? '_timestamp' : xAxis,
      metricTransformations:
        runsLinePlotConfig != null && enableBackendSmoothing
          ? getMetricTransformations(runsLinePlotConfig, metricKey)
          : undefined,
      groupArea: runsLinePlotConfig?.groupArea,
    };
  }
  return spec;
}
export function defaultTitle(
  runsLinePlotConfig: Pick<RunsLinePlotConfig, 'metrics' | 'expressions'>
): string {
  // eslint-disable-next-line no-extra-boolean-cast
  const metricsInUse = Boolean(runsLinePlotConfig.expressions?.[0])
    ? runsLinePlotConfig.expressions
    : runsLinePlotConfig.metrics;

  return metricsInUse?.join(', ') ?? '';
}

export function getHasSystemMetrics(
  runsLinePlotConfig: Pick<RunsLinePlotConfig, 'metrics'>
): boolean {
  if (runsLinePlotConfig.metrics == null) {
    return false;
  }
  return runsLinePlotConfig.metrics.some(isSystemMetric);
}

export function getTitleFromConfig(
  runsLinePlotConfig: RunsLinePlotConfig
): string {
  return runsLinePlotConfig.chartTitle || defaultTitle(runsLinePlotConfig);
}
